import { FieldType, DraftProcurationFeeRule, ProcurationFeeItem, ProcurationFeeItemType, FeeUnit } from '@pmp/api';
import { CrudMode, transformUppercaseNoSpaces } from '@ohpen/enso-ui';
import type { FormItem, RenderFormItem, SelectOption, SelectsOptions } from '@ohpen/enso-ui';
import type { FormInstance } from 'antd';
import type { FormListFieldData } from 'antd/es/form/FormList';
import { t } from 'i18next';
import { DEFAULT_TEXT_MAX_LENGTH_SHORT, textLimitProps } from '@pmp/utils';

export const getFeesFormSchema = (selectsOptions: SelectsOptions, mode: CrudMode | null, showAlert: boolean): FormItem[] => {
  const disabled = mode === CrudMode.VIEW;

  return [
    {
      valueType: 'alert',
      fieldProps: { message: t('lender.procurationFees.labels.procurationFeeInUseMessage'), type: 'warning', showIcon: true },
      formItemProps: { hidden: !showAlert },
    },
    {
      dataIndex: 'id',
      title: t('lender.procurationFees.labels.id'),
      fieldProps: { disabled: true },
      formItemProps: { getValueFromEvent: transformUppercaseNoSpaces, hidden: mode === CrudMode.CREATE },
    },
    {
      dataIndex: 'name',
      title: t('lender.procurationFees.labels.name'),
      fieldProps: { disabled: mode === CrudMode.VIEW, ...textLimitProps(DEFAULT_TEXT_MAX_LENGTH_SHORT) },
      formItemProps: { required: true },
    },
    {
      dataIndex: 'feeItems',
      valueType: 'formList',
      initialValue: [
        {
          index: 0,
          procurationFeeItemType: 'COMPLETION',
          value: undefined,
          unit: undefined,
          basis: undefined,
        },
      ],
      listProps: {
        min: 1,
        creatorButtonProps: {
          type: 'default',
          size: 'small',
          creatorButtonText: t('lender.procurationFees.buttons.addFee'),
        },
        deleteIconProps: (_meta: FormListFieldData, index: number, form: FormInstance) => {
          const feeItems = (form?.getFieldValue('feeItems') as ProcurationFeeItem[]) ?? [];
          const isCompletion = feeItems[index]?.procurationFeeItemType === ProcurationFeeItemType.COMPLETION;
          return isCompletion ? false : {};
        },
        deleteTooltip: t('enso.buttons.delete'),
      },
      columns: (_meta: FormListFieldData, index: number, form: FormInstance) => {
        const feeItems = (form?.getFieldValue('feeItems') as ProcurationFeeItem[]) ?? [];
        const currentType = feeItems[index]?.procurationFeeItemType;
        const currentUnit = feeItems[index]?.unit;
        const currentBasis = feeItems[index]?.basis;
        const isCompletion = currentType === ProcurationFeeItemType.COMPLETION;
        const isFlat = currentUnit === FeeUnit.FLAT;

        const completionFeeIndex = Array.isArray(feeItems)
          ? feeItems.findIndex(fee => fee.procurationFeeItemType === ProcurationFeeItemType.COMPLETION)
          : -1;

        const typeOptions = selectsOptions[FieldType.PROCURATION_FEE_ITEM_TYPE].map((option: SelectOption) => ({
          ...option,
          disabled: completionFeeIndex > -1 && completionFeeIndex !== index && option.value === ProcurationFeeItemType.COMPLETION,
        }));

        const basisOptions = isCompletion
          ? selectsOptions[FieldType.PROCURATION_FEE_BASIS].filter(x => x.value !== 'OUTSTANDING_BALANCE')
          : selectsOptions[FieldType.PROCURATION_FEE_BASIS];

        const unitOptions = selectsOptions[FieldType.PROCURATION_FEE_UNIT].map((option: SelectOption) => ({
          ...option,
          disabled: currentBasis && option.value === FeeUnit.FLAT,
        }));

        const nonCompletionColumns: FormItem[] = isCompletion
          ? []
          : [
              {
                dataIndex: 'startingMonth',
                title: t('lender.procurationFees.labels.startingMonth'),
                valueType: 'digit' as const,
                colProps: { span: 8 },
                formItemProps: { required: true },
                fieldProps: { min: 1, max: 12, step: 1, disabled },
              },
              {
                dataIndex: 'intervalInMonths',
                title: t('lender.procurationFees.labels.periodInMonths'),
                valueType: 'digit' as const,
                colProps: { span: 8 },
                formItemProps: { required: true },
                fieldProps: { precision: 0, min: 1, step: 1, disabled },
              },
              {
                dataIndex: 'endingType',
                title: t('lender.procurationFees.labels.endingType'),
                valueType: 'select' as const,
                colProps: { span: 8 },
                formItemProps: { required: true },
                fieldProps: { options: selectsOptions[FieldType.ENDING_TYPE], disabled },
              },
            ];

        return [
          {
            dataIndex: 'procurationFeeItemType',
            title: t('lender.procurationFees.labels.type'),
            valueType: 'select' as const,
            colProps: { span: 24 },
            formItemProps: { required: true },
            fieldProps: { options: typeOptions, allowClear: false, disabled: disabled || isCompletion },
          },
          {
            dataIndex: 'unit',
            title: t('lender.procurationFees.labels.unit'),
            valueType: 'select' as const,
            colProps: { span: 8 },
            formItemProps: { required: true },
            fieldProps: { options: unitOptions, disabled },
          },
          {
            dataIndex: 'value',
            title: t('lender.procurationFees.labels.value'),
            valueType: 'digit' as const,
            colProps: { span: 8 },
            formItemProps: { required: true },
            fieldProps: { min: -999999999, disabled },
          },
          {
            dataIndex: 'basis',
            title: t('lender.procurationFees.labels.basis'),
            valueType: 'select' as const,
            colProps: { span: 8 },
            fieldProps: { options: basisOptions, disabled: disabled || isFlat },
          },
          ...nonCompletionColumns,
        ];
      },
    },
  ];
};

export const getFeeRulesFormSchema = (
  selectsOptions: SelectsOptions,
  renderAlert: RenderFormItem<DraftProcurationFeeRule>,
  mode?: CrudMode | null,
): FormItem[] => {
  const disabled = mode === CrudMode.VIEW;

  return [
    {
      renderFormItem: renderAlert,
    },
    {
      title: 'lender.procurationFees.labels.id',
      dataIndex: 'id',
      fieldProps: {
        disabled: true,
      },
      formItemProps: {
        hidden: mode === CrudMode.CREATE,
      },
    },
    {
      title: 'lender.procurationFees.labels.name',
      dataIndex: 'name',
      formItemProps: {
        required: true,
      },
      fieldProps: {
        disabled,
        ...textLimitProps(DEFAULT_TEXT_MAX_LENGTH_SHORT),
      },
    },
    {
      title: 'lender.procurationFees.labels.operationType',
      dataIndex: 'operationType',
      valueType: 'select',
      tooltip: t('lender.procurationFees.labels.operationTypeTooltip'),
      fieldProps: {
        options: selectsOptions[FieldType.OPERATION_TYPE],
        disabled,
      },
    },
    {
      title: 'lender.procurationFees.labels.applicationType',
      dataIndex: 'applicationType',
      valueType: 'select',
      tooltip: t('lender.procurationFees.labels.applicationTypeTooltip'),
      fieldProps: {
        options: selectsOptions[FieldType.APPLICATION_TYPE],
        disabled,
      },
    },
    {
      title: 'lender.procurationFees.labels.propertyPurpose',
      dataIndex: 'propertyPurpose',
      valueType: 'select',
      tooltip: t('lender.procurationFees.labels.propertyPurposeTooltip'),
      fieldProps: {
        options: selectsOptions[FieldType.PROPERTY_PURPOSE],
        disabled,
      },
    },
  ];
};
